/*
 * Bulk CSV upload dialog — alternative to Shopify-connected ingestion.
 *
 * Buyer drops a CSV file with order rows; we parse client-side via PapaParse,
 * validate against a known schema, then POST to the preview endpoint to get
 * the seller's catalog reconciliation (which SKUs match, totals, etc).
 *
 * Required columns: customer_name, customer_email, address_line1, city,
 *                   state, postal_code, country, sku, qty
 * Optional:         customer_phone, address_line2, customer_id, note
 *
 * Multiple rows for the same customer (matched by email + address_line1)
 * get auto-grouped into one shipment with summed qty per SKU.
 */
import { useState, useRef, useEffect } from "react";
import Papa from "papaparse";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import api, { money } from "../../lib/api";
import { toast } from "sonner";
import { Upload, FileText, AlertTriangle, CheckCircle2, X, Download } from "lucide-react";

const REQUIRED_COLS = [
  "customer_name", "customer_email", "address_line1", "city",
  "state", "postal_code", "country", "sku", "qty",
];
const OPTIONAL_COLS = [
  "customer_phone", "address_line2", "customer_id", "note", "order_id",
];

const SAMPLE_CSV =
  REQUIRED_COLS.concat(OPTIONAL_COLS).join(",") + "\n" +
  '"Sarah Patel","sarah@example.com","241 Palmer Ave","Brooklyn","NY","11201","US","GLOW-50ML","2","555-0142","Apt 3B","CUST-001","Gift wrap please","ORDER-1001"\n' +
  '"Sarah Patel","sarah@example.com","241 Palmer Ave","Brooklyn","NY","11201","US","BLOOM-SET","1","555-0142","Apt 3B","CUST-001","","ORDER-1001"\n' +
  '"Marcus Lee","marcus@example.com","2290 Kenmore","Buffalo","NY","14216","US","GLOW-50ML","1","","","","","ORDER-1002"\n';

export default function CsvUploadDialog({ open, onClose, onSuccess, sellers, initialSellerId }) {
  const [step, setStep] = useState("pick");  // pick | preview | uploading | done
  const [sellerId, setSellerId] = useState(initialSellerId || sellers?.[0]?.id || "");
  const [rows, setRows] = useState([]);
  const [parseErrors, setParseErrors] = useState([]);
  const [missingCols, setMissingCols] = useState([]);
  const [previewData, setPreviewData] = useState(null);  // server response
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const dragRef = useRef(null);
  // Reset selected seller whenever caller passes a new initialSellerId
  // (e.g. clicking Re-upload on a different supplier in the history drawer).
  useEffect(() => {
    if (open && initialSellerId) setSellerId(initialSellerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialSellerId]);

  const reset = () => {
    setStep("pick");
    setRows([]);
    setParseErrors([]);
    setMissingCols([]);
    setPreviewData(null);
    setSubmitting(false);
  };

  const handleFile = (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv") && file.type !== "text/csv") {
      toast.error("Please pick a .csv file");
      return;
    }
    Papa.parse(file, {
      header: true,
      skipEmptyLines: "greedy",
      transformHeader: h => h.trim().toLowerCase(),
      complete: (results) => {
        const headers = results.meta.fields || [];
        const missing = REQUIRED_COLS.filter(c => !headers.includes(c));
        setMissingCols(missing);
        const rawRows = results.data || [];
        setParseErrors(results.errors || []);
        if (missing.length === 0) {
          // Coerce qty to int + trim string fields.
          const cleaned = rawRows.map(r => {
            const out = { qty: parseInt(r.qty, 10) || 0 };
            for (const c of [...REQUIRED_COLS, ...OPTIONAL_COLS]) {
              if (c === "qty") continue;
              out[c] = (r[c] ?? "").toString().trim() || (OPTIONAL_COLS.includes(c) ? null : "");
            }
            return out;
          }).filter(r => r.qty > 0 && r.customer_email && r.sku);
          setRows(cleaned);
          if (cleaned.length === 0) {
            toast.error("No valid rows found in the CSV");
            return;
          }
          // Auto-call preview now.
          callPreview(cleaned);
        }
      },
      error: (err) => {
        toast.error("CSV parse failed: " + err.message);
      },
    });
  };

  const callPreview = async (parsedRows) => {
    if (!sellerId) {
      toast.error("Pick a seller first");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/dropship/orders/upload-csv/preview", {
        seller_id: sellerId,
        rows: parsedRows,
      });
      setPreviewData(r.data);
      setStep("preview");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally {
      setSubmitting(false);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    setStep("uploading");
    try {
      const r = await api.post("/dropship/orders/upload-csv", {
        seller_id: sellerId,
        rows,
      });
      const overridden = r.data.overridden_count || 0;
      const created = r.data.orders.length - overridden;
      let msg = `Created ${created} order(s) from ${r.data.summary.shipment_count} customer(s)`;
      if (overridden > 0) msg += ` · ${overridden} existing order(s) updated`;
      toast.success(msg);
      onSuccess && onSuccess(r.data);
      reset();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
      setStep("preview");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "verqo-bulk-orders-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent className="max-w-3xl" data-testid="csv-upload-dialog">
        <DialogHeader>
          <DialogTitle>Upload orders via CSV</DialogTitle>
        </DialogHeader>

        {/* Seller picker */}
        {(sellers?.length || 0) > 1 && step === "pick" && (
          <div className="mt-3">
            <label className="text-[11.5px] mono uppercase tracking-[0.14em] text-foreground/55">Supplier</label>
            <select value={sellerId} onChange={e => setSellerId(e.target.value)}
                    className="mt-1 w-full h-10 rounded-md border border-border px-3 text-[13px]"
                    data-testid="csv-seller-select">
              {(sellers || []).map(s => (
                <option key={s.id} value={s.id}>{s.company || s.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* PICK step */}
        {step === "pick" && (
          <div className="mt-3 space-y-3">
            <div
              ref={dragRef}
              onDragOver={e => { e.preventDefault(); dragRef.current?.classList.add("border-foreground"); }}
              onDragLeave={() => dragRef.current?.classList.remove("border-foreground")}
              onDrop={e => {
                e.preventDefault();
                dragRef.current?.classList.remove("border-foreground");
                handleFile(e.dataTransfer.files?.[0]);
              }}
              onClick={() => fileInputRef.current?.click()}
              className="rounded-2xl border-2 border-dashed border-border bg-secondary/30 p-10 text-center cursor-pointer hover:border-foreground/40 transition-colors"
              data-testid="csv-drop-zone">
              <Upload className="w-10 h-10 mx-auto text-foreground/40 mb-3"/>
              <div className="text-[15px] font-medium">Drop your CSV here</div>
              <div className="text-[12.5px] text-foreground/55 mt-1">or click to browse</div>
              <input ref={fileInputRef} type="file" accept=".csv,text/csv" hidden
                     onChange={e => handleFile(e.target.files?.[0])}
                     data-testid="csv-file-input"/>
            </div>

            <div className="rounded-xl border border-border bg-white p-3 text-[12px] flex items-start gap-2">
              <FileText className="w-4 h-4 mt-0.5 text-foreground/55 shrink-0"/>
              <div className="flex-1">
                <div className="font-medium text-foreground/80 mb-1">Required columns</div>
                <div className="mono text-[11.5px] text-foreground/65">{REQUIRED_COLS.join(" · ")}</div>
                <div className="font-medium text-foreground/80 mt-2 mb-1">Optional columns</div>
                <div className="mono text-[11.5px] text-foreground/55">{OPTIONAL_COLS.join(" · ")}</div>
              </div>
              <button onClick={downloadSample} data-testid="download-sample-csv"
                      className="text-[11.5px] mono uppercase tracking-[0.14em] px-3 py-1.5 rounded-full border border-border hover:bg-secondary inline-flex items-center gap-1.5">
                <Download className="w-3 h-3"/>Sample
              </button>
            </div>

            {missingCols.length > 0 && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-[12.5px] text-rose-900 flex gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5"/>
                <div>
                  <strong>Missing required columns:</strong> {missingCols.join(", ")}.
                  Download the sample CSV to see the expected format.
                </div>
              </div>
            )}

            <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-3 text-[12px] text-indigo-900">
              <div className="font-medium mb-1">Override existing orders without duplicating</div>
              <div className="text-indigo-900/80">
                Include an <span className="mono bg-white/60 px-1.5 py-0.5 rounded">order_id</span> column
                in your CSV. If you re-upload a row with the same <span className="mono">order_id</span> for the same
                buyer + seller + SKU, we'll <strong>update</strong> the existing order (qty + address) instead of
                creating a duplicate. Already-shipped orders are never overridden.
              </div>
            </div>
          </div>
        )}

        {/* PREVIEW step */}
        {step === "preview" && previewData && (
          <div className="mt-3 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <SummaryTile label="Customers" value={previewData.summary.shipment_count}/>
              <SummaryTile label="Items" value={previewData.summary.total_items}/>
              <SummaryTile label="Errors" value={previewData.summary.error_count || 0}
                           tone={(previewData.summary.error_count || 0) > 0 ? "rose" : "default"}/>
              <SummaryTile label="Wholesale total" value={money(previewData.summary.total_wholesale)}/>
            </div>

            {/* Row-level errors — block upload if any are present */}
            {previewData.errors?.length > 0 && (
              <div className="rounded-xl border border-rose-300 bg-rose-50 p-3.5 text-[12.5px]" data-testid="csv-row-errors">
                <div className="font-medium text-rose-900 flex items-center gap-1.5 mb-2">
                  <AlertTriangle className="w-4 h-4"/>
                  {previewData.errors.length} row{previewData.errors.length !== 1 ? "s" : ""} blocked — fix and re-upload
                </div>
                <div className="rounded-lg border border-rose-200 bg-white max-h-48 overflow-y-auto">
                  <div className="grid grid-cols-[60px_1fr_1fr_2fr] gap-2 px-3 py-1.5 bg-rose-100/50 text-[10.5px] mono uppercase tracking-[0.14em] text-rose-900/70 sticky top-0">
                    <div>Row</div>
                    <div>SKU</div>
                    <div>Customer</div>
                    <div>Reason</div>
                  </div>
                  {previewData.errors.map((e, i) => (
                    <div key={i} className="grid grid-cols-[60px_1fr_1fr_2fr] gap-2 px-3 py-1.5 text-[11.5px] border-b border-rose-100 last:border-0"
                         data-testid={`csv-error-row-${e.row}`}>
                      <div className="mono">{e.row}</div>
                      <div className="mono truncate">{e.sku}</div>
                      <div className="truncate text-foreground/70">{e.customer_email}</div>
                      <div className="text-rose-900 truncate" title={e.message}>
                        {e.reason === "sku_not_in_catalog"  ? "SKU not in catalog — pick a valid SKU"
                         : e.reason === "out_of_stock"     ? `Only ${e.available} in stock (asked ${e.qty})`
                         :                                   e.message}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-2 text-[11.5px] text-rose-900/80">
                  Edit your CSV to fix these rows then click <strong>Re-upload</strong>. Orders won't be created until all rows pass.
                </div>
              </div>
            )}

            {/* Shipments table — only shown when there ARE valid shipments */}
            {previewData.shipments.length > 0 && (
              <div>
                <div className="text-[11px] mono uppercase tracking-[0.14em] text-foreground/55 mb-2">
                  {previewData.errors?.length > 0 ? "Rows that will create orders" : "Order preview"}
                </div>
                <div className="rounded-xl border border-border bg-white max-h-72 overflow-y-auto">
                  <div className="grid grid-cols-[1.5fr_2fr_1fr_100px] gap-3 px-3 py-2 bg-secondary/50 border-b border-border text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55 sticky top-0">
                    <div>Customer</div>
                    <div>Ship to</div>
                    <div>Items</div>
                    <div className="text-right">Subtotal</div>
                  </div>
                  {previewData.shipments.map((s, i) => {
                    const sub = s.items.reduce((a, it) => a + it.qty * it.wholesale_cost, 0);
                    return (
                      <div key={i} className="grid grid-cols-[1.5fr_2fr_1fr_100px] gap-3 px-3 py-2.5 border-b border-border last:border-0 text-[12.5px]"
                           data-testid={`csv-preview-shipment-${i}`}>
                        <div className="min-w-0">
                          <div className="font-medium truncate">{s.customer_name}</div>
                          <div className="text-[11px] text-foreground/55 truncate">{s.customer_email}</div>
                          {s.external_order_id && (
                            <div className="text-[10px] mono text-indigo-700 mt-0.5">
                              order id: {s.external_order_id}
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 text-foreground/70 text-[11.5px]">
                          <div className="truncate">{s.ship_to.line1}{s.ship_to.line2 ? `, ${s.ship_to.line2}` : ""}</div>
                          <div className="truncate">{s.ship_to.city}, {s.ship_to.state} {s.ship_to.postal_code} · {s.ship_to.country}</div>
                        </div>
                        <div className="text-foreground/70 text-[11.5px]">
                          {s.items.map((it, j) => (
                            <div key={j} className="truncate">{it.qty}× <span className="mono">{it.sku}</span></div>
                          ))}
                        </div>
                        <div className="text-right font-medium">{money(sub)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {step === "uploading" && (
          <div className="py-12 text-center">
            <div className="text-[14px]">Creating orders…</div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4 border-t border-border mt-4">
          <Button variant="outline" onClick={() => { reset(); onClose(); }} className="rounded-full h-9 px-4" data-testid="csv-cancel">
            <X className="w-3.5 h-3.5 mr-1"/>Cancel
          </Button>
          {step === "preview" && previewData?.errors?.length > 0 && (
            <Button variant="outline"
                    onClick={() => { reset(); /* drop back to picker so user can fix the file and try again */ }}
                    className="rounded-full h-9 px-4 border-rose-300 text-rose-700 hover:bg-rose-50"
                    data-testid="csv-reupload">
              <Upload className="w-3.5 h-3.5 mr-1"/>Fix &amp; re-upload
            </Button>
          )}
          {step === "preview" && previewData?.shipments?.length > 0 && !previewData?.errors?.length && (
            <Button onClick={submit} disabled={submitting}
                    className="rounded-full h-9 px-4 bg-foreground text-background hover:bg-foreground/90"
                    data-testid="csv-confirm-upload">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1"/>
              Create {previewData.summary.shipment_count} shipment{previewData.summary.shipment_count !== 1 ? "s" : ""}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SummaryTile({ label, value, tone = "default" }) {
  const cls = tone === "amber" ? "border-amber-200 bg-amber-50"
            : tone === "rose"  ? "border-rose-300 bg-rose-50"
            :                     "border-border bg-white";
  return (
    <div className={`rounded-xl border ${cls} p-3`}>
      <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">{label}</div>
      <div className="display text-xl mt-0.5 tracking-tight">{value}</div>
    </div>
  );
}
